import type { Filter } from '@unifyapps/network/generated/models/filter';
import type { Sort } from '@unifyapps/network/generated/models/sort';
import type { UIFilter } from '@unifyapps/network/generated/models/uIFilter';
import type {
  BlockComponentStateId,
  BlockComponentType,
  ComputedVisibilityPayloadType,
} from '../../types/block';
import type { DateTimeAddOns } from '../../types/date';
import type { InterfacePermissions } from '../../types/interface';
import type { StyleObject } from '../../types/styleObject';
import type { ComputedButtonAppearance } from '../Button/types';

// Order matters here because we use this order to render the selection in properties panel
export enum FilterActionsEnum {
  AIFilter = 'ai',
  ColumnVisibility = 'columnVisibility',
  RowHeight = 'rowHeight',
  Views = 'views',
  QuickFilter = 'quick',
  FieldFilter = 'field',
  DataFilter = 'data',
  SortFilter = 'sort',
  FilterAndSort = 'filterAndSort',
  DateFilter = 'date',
  KeywordSearch = 'search',
}

export enum AggregationMetadataSourceType {
  Manual = 'manual',
  Mapped = 'mapped',
}

export type FilterAdapters<F = Filter | null | undefined> = {
  /**
   * This adapter is used to get notified when the filters are changed, and new filters are applied.
   * @param filter - The new filter value
   * @returns The input filter back
   */
  onApplyNewFilters: (filter: F) => F;
};

export type FilterMenuOptions = {
  /**
   * List of ids for Upfront Filters
   */
  visibleFilterIds?: string[];
  /**
   * Flag to hide FilterMenu when visibleFilterIds are present
   */
  hideFilterMenu?: boolean;
  /**
   * Adapters for the FilterMenu
   */
  adapters?: FilterAdapters;
};

export interface BaseFilterActionsType {
  id: string;
  type: FilterActionsEnum;
  label?: string;
  appliedLabel?: string;
  valueSource?: ValueSource;
  permissions?: InterfacePermissions;
}

export type FilterButtonAppearance = {
  size?: 'sm' | 'md';
  disabled?: boolean;
  visibility?: ComputedVisibilityPayloadType;
};

export type DataFilterInitialValue = {
  filter?: Filter;
};
export type DataQuickFilterInitialValue = {
  filter?: Filter;
  uiFilter?: UIFilter;
};

export type SortFilterInitialValue = {
  sort?: Sort[];
};

export interface DataFilterAction extends BaseFilterActionsType, FilterMenuOptions {
  type: FilterActionsEnum.DataFilter;
  initialValue?: DataFilterInitialValue;
  mappedInitialValue?: Filter;
  appearance?: FilterButtonAppearance;
  showOnlyVisibleFields?: boolean;
  dateTimeAddOns?: DateTimeAddOns;
  //  List of field names that are permitted to be displayed in the filter options
  allowedFilterFields?: string[];
}

export interface SortFilterAction extends BaseFilterActionsType {
  type: FilterActionsEnum.SortFilter;
  appearance?: FilterButtonAppearance;
  showOnlyVisibleFields?: boolean;
  initialValue?: SortFilterInitialValue;
  mappedInitialValue?: SortFilterInitialValue | Sort[];
}

// full ordered field list; per-field `hidden` drives visibility (no separate hide flag)
export interface FilterMenuField {
  key: string;
  hidden?: boolean;
  enabledOperators?: string[];
}

export interface SortMenuField {
  key: string;
  hidden?: boolean;
}

// inline (upfront) filter field with its own operators
export interface VisibleFilterField {
  key: string;
  enabledOperators?: string[];
}

export interface FilterAndSortAction extends BaseFilterActionsType {
  type: FilterActionsEnum.FilterAndSort;
  enableFilter?: boolean;
  initialFilterValue?: DataFilterInitialValue;
  filterMenuFields?: FilterMenuField[];
  visibleFilterIds?: VisibleFilterField[];
  enableSort?: boolean;
  sortMenuFields?: SortMenuField[];
}

export interface AIFilterAction {
  type: FilterActionsEnum.AIFilter;
  id: string;
  label: string;
  appearance?: FilterButtonAppearance;
  permissions?: InterfacePermissions;
}

export interface ColumnVisibilityAction extends BaseFilterActionsType {
  type: FilterActionsEnum.ColumnVisibility;
  initialHiddenColumns?: string[] | { type: 'filter'; payload: UIFilter };
}

export interface RowHeightAction extends BaseFilterActionsType {
  type: FilterActionsEnum.RowHeight;
}

export interface ViewsAction extends BaseFilterActionsType {
  type: FilterActionsEnum.Views;
  icon?: string;
  mainViewLabel?: string;
}

export enum DateRangePresets {
  Today = 'today',
  Last24hours = 'last24hours',
  Yesterday = 'yesterday',
  Last7Days = 'last7days',
  Last30Days = 'last30days',
  ThisMonth = 'thismonth',
  MonthToDate = 'monthToDate',
  LastMonth = 'lastmonth',
  ThisQuarter = 'thisquarter',
  LastQuarter = 'lastquarter',
  ThisYear = 'thisyear',
  LastYear = 'lastyear',
  Lifetime = 'lifetime',
  Last15Days = 'last15days',
  Last90Days = 'last90days',
  Last180Days = 'last180days',
}

export enum DateRangePeriod {
  Previous = 'previous',
  Upcoming = 'upcoming',
}

export enum TimeRangeItems {
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export type DateRange = {
  since?: Date;
  until?: Date;
};

export type PresetsWithCustom = DateRangePresets | 'custom' | 'fixed';
export type InitialDateRangePreset = PresetsWithCustom | null;
export type InitialDateRangeValue = DateRange | null;

export enum ValueSource {
  Custom = 'custom',
  Mapped = 'mapped',
}

export type DateRangeInitialValue = {
  preset?: PresetsWithCustom;
  fixedRange?: {
    since?: string;
    until?: string;
  };
  period?: DateRangePeriod;
  range?: {
    value?: number;
    item?: TimeRangeItems;
  };
};

export type MappedDateFilterInitialValue = {
  preset?: DateRangePresets;
  startTime?: string | number;
  endTime?: string | number;
  previousStartTime?: string | number;
  previousEndTime?: string | number;
};

export interface DateFilterAction extends BaseFilterActionsType {
  type: FilterActionsEnum.DateFilter;
  variant?: 'date-time-range' | 'date-range' | 'date' | 'date-time';
  enablePresets?: boolean;
  presetDateOptions?: DateRangePresets[];
  enableClear?: boolean;
  enableReset?: boolean;
  initialValue?: DateRangeInitialValue;
  mappedInitialValue?: MappedDateFilterInitialValue;
  appearance?: FilterButtonAppearance;
  dateTimeAddOns?: DateTimeAddOns;
}

export interface SearchFilterAction extends BaseFilterActionsType {
  type: FilterActionsEnum.KeywordSearch;
  placeholder?: string;
  initialValue?: string;
  appearance?: {
    size?: 'sm' | 'md';
    disabled?: boolean;
    visibility?: ComputedVisibilityPayloadType;
  };
}

export interface QuickFilterDataAction {
  initialValue?: DataQuickFilterInitialValue;
  mappedInitialValue?: Filter;
}

export interface QuickFilterDateAction {
  initialValue?: DateRangeInitialValue;
  mappedInitialValue?: MappedDateFilterInitialValue;
}

export interface QuickFilterAction extends BaseFilterActionsType {
  type: FilterActionsEnum.QuickFilter;
  filterType: FilterActionsEnum.DataFilter | FilterActionsEnum.DateFilter;
  dateFilter?: QuickFilterDateAction;
  dataFilter?: QuickFilterDataAction;
  appearance?: Omit<ComputedButtonAppearance, 'variant' | 'color'>;
  preSelectFilter?: boolean;
}

export interface FieldFilterAction extends BaseFilterActionsType {
  type: FilterActionsEnum.FieldFilter;
  fieldId?: string;
  // valid operators for a field filters, when presents,
  // these are the only operators shown to the user to choose operator from
  enabledOperators?: string[];
  appearance?: Omit<ComputedButtonAppearance, 'variant' | 'color'>;
  filter?: Filter;
}

export type FilterBlockContent = {
  dataSourceIds?: string[];
  primaryActions: (
    | DataFilterAction
    | DateFilterAction
    | SearchFilterAction
    | QuickFilterAction
    | SortFilterAction
    | FilterAndSortAction
    | AIFilterAction
    | ColumnVisibilityAction
    | RowHeightAction
    | FieldFilterAction
    | ViewsAction
  )[];
  persistFilters?: boolean;
};

type FilterBlockAppearance = {
  disabled?: boolean;
  styles?: Pick<StyleObject, 'padding' | 'width' | 'borderRadius'>;
};

export type FiltersComponentType = BlockComponentType<
  'Filters',
  FilterBlockContent,
  FilterBlockAppearance
>;

export type FiltersBlockStateType = Pick<
  FiltersComponentType,
  'content' | 'appearance' | 'componentType'
> &
  BlockComponentStateId & {
    state?: {
      filter?: unknown;
      filterMenuState?: unknown;
      search?: string;
      sort?: unknown[];
      dateTimeFilter?: {
        startTime?: string;
        endTime?: string;
        previousStartTime?: string;
        previousEndTime?: string;
        iso?: {
          startTime?: string;
          endTime?: string;
          previousStartTime?: string;
          previousEndTime?: string;
        };
        epoch?: {
          startTime?: number;
          endTime?: number;
          previousStartTime?: number;
          previousEndTime?: number;
        };
      };
      // For FE use case
      dateRangePreset?: DateRangePresets | null;
      dataQuickFilter?: QuickFilterAction | null;
      dateQuickFilter?: QuickFilterAction | null;
      fieldFilters?: FieldFilterAction[] | null;
    };
  };

export type FiltersBlockSessionState = FiltersBlockStateType['state'];

export type FiltersBlockRef = {
  persistState: (state: FiltersBlockSessionState) => void;
};
